/**
 * PageAccessGuard
 *
 * Generic route-level guard that shows the centered "not authorized" banner
 * when the user lacks `view_actions` (eye toggle) for `feature` — or for a
 * specific `subFeature` when provided. Backed by useCanAccess, so it picks
 * up assumed-role + feature-flag logic for free.
 *
 * Usage in App.tsx:
 *   <Route path="/skills" element={
 *     <PageAccessGuard feature="skills"><SkillsPage /></PageAccessGuard>
 *   } />
 *
 *   <Route path="/agent-studio/auto-tune" element={
 *     <PageAccessGuard feature="agent-studio" subFeature="auto_tune">
 *       <AutoTunePage />
 *     </PageAccessGuard>
 *   } />
 */

import React, { type ReactNode } from 'react'
import { useCanAccess } from '../../hooks/useCanAccess'
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext'
import { isFlagEffectivelyEnabled, type FeatureFlagKey } from '../../constants/featureFlags'
import { UnauthorizedBanner } from './UnauthorizedBanner'

interface PageAccessGuardProps {
  /** Feature id (matches `feature` arg of useCanAccess), e.g. "skills". */
  feature: string
  /** Optional sub-feature key. When set, checks `canViewUI(feature, subFeature)`. */
  subFeature?: string
  /**
   * Additional child keys that also grant the route. Used when a parent page
   * must stay reachable for a dedicated child (e.g. Benchmarks-only roles
   * hitting `/evals-simulations` so they can redirect to `/benchmarks`).
   */
  orSubFeatures?: string[]
  /**
   * When set, also require this tenant flag to be REALLY enabled (bypassing the
   * global isEnabled kill-switch). Shows the lock banner when the flag is off.
   * Opt-in — existing guards pass nothing and keep their RBAC-only behaviour.
   */
  enforceFlag?: FeatureFlagKey
  /**
   * When true, skip the RBAC `canViewUI` check entirely — only the `enforceFlag`
   * tenant-flag gate applies. Use for features that must be accessible to every
   * authenticated user regardless of role permissions (e.g. Docs).
   */
  bypassRbac?: boolean
  children: ReactNode
}

function rbacAllowsView(
  canViewUI: (feature: string, subFeature?: string) => boolean,
  feature: string,
  subFeature: string | undefined,
  orSubFeatures: string[] | undefined,
): boolean {
  if (canViewUI(feature, subFeature)) return true
  return Boolean(orSubFeatures?.some((child) => canViewUI(feature, child)))
}

export const PageAccessGuard: React.FC<PageAccessGuardProps> = ({
  feature,
  subFeature,
  orSubFeatures,
  enforceFlag,
  bypassRbac,
  children,
}) => {
  const { canViewUI, isLoading } = useCanAccess()
  const { flags, isLoading: isFlagsLoading } = useFeatureFlags()
  if (isLoading || isFlagsLoading) return null
  if (enforceFlag && !isFlagEffectivelyEnabled(enforceFlag, flags)) return <UnauthorizedBanner />
  if (!bypassRbac && !rbacAllowsView(canViewUI, feature, subFeature, orSubFeatures)) {
    return <UnauthorizedBanner />
  }
  return <>{children}</>
}

export default PageAccessGuard
